// Config-driven engine for the "extra" unit converters that do not fit the
// linear-factor model in browser-unit-tools.ts. Each variant declares its own
// unit set plus a converter closure so one component can drive them all:
//   - digital-converter    linear factor, but base-dependent (decimal vs binary)
//   - each-converter        pure linear counting factors
//   - pace-converter        pace units are the INVERSE of speed (special math)
//   - parts-per-converter   pure linear concentration factors
// All math runs in the browser; nothing is uploaded.

export type ExtraUnitVariant =
  | 'digital-converter'
  | 'each-converter'
  | 'pace-converter'
  | 'parts-per-converter';

export type DigitalBase = 'decimal' | 'binary';

export type ExtraUnit = {
  id: string;
  name: string;
  symbol: string;
};

export type ExtraUnitConfig = {
  badge: string;
  heading: string;
  subtitle: string;
  measure: string;
  units: ExtraUnit[];
  defaultFrom: string;
  defaultTo: string;
  sampleValue: number;
  hasBaseToggle: boolean;
  // Convert `value` expressed in `fromId` into `toId`. `baseMode` only matters
  // for the digital converter; other variants ignore it.
  convert: (value: number, fromId: string, toId: string, baseMode: DigitalBase) => number;
};

// --- digital storage --------------------------------------------------------

const DIGITAL_BITS: Record<string, { dec: number; bin: number }> = {
  bit: { dec: 1, bin: 1 },
  byte: { dec: 8, bin: 8 },
  kilobit: { dec: 1e3, bin: 1024 },
  kilobyte: { dec: 8e3, bin: 8 * 1024 },
  megabit: { dec: 1e6, bin: 1024 ** 2 },
  megabyte: { dec: 8e6, bin: 8 * 1024 ** 2 },
  gigabit: { dec: 1e9, bin: 1024 ** 3 },
  gigabyte: { dec: 8e9, bin: 8 * 1024 ** 3 },
  terabyte: { dec: 8e12, bin: 8 * 1024 ** 4 },
  petabyte: { dec: 8e15, bin: 8 * 1024 ** 5 },
};

function convertDigital(
  value: number,
  fromId: string,
  toId: string,
  baseMode: DigitalBase,
): number {
  const key = baseMode === 'binary' ? 'bin' : 'dec';
  const from = DIGITAL_BITS[fromId];
  const to = DIGITAL_BITS[toId];
  if (!from || !to) return Number.NaN;
  const bits = value * from[key];
  return bits / to[key];
}

// --- each / counting --------------------------------------------------------

const EACH_FACTOR: Record<string, number> = {
  each: 1,
  pair: 2,
  'half-dozen': 6,
  dozen: 12,
  'bakers-dozen': 13,
  score: 20,
  gross: 144,
  'great-gross': 1728,
  ream: 500,
};

// --- parts-per / concentration ---------------------------------------------

const PARTS_FACTOR: Record<string, number> = {
  percent: 1e-2,
  'per-mille': 1e-3,
  ppm: 1e-6,
  ppb: 1e-9,
  ppt: 1e-12,
  'mg-kg': 1e-6, // 1 mg/kg == 1 ppm by mass
  'mg-l': 1e-6, // 1 mg/L ≈ 1 ppm for dilute aqueous solutions
};

function convertLinear(
  factors: Record<string, number>,
  value: number,
  fromId: string,
  toId: string,
): number {
  const from = factors[fromId];
  const to = factors[toId];
  if (!from || !to) return Number.NaN;
  return (value * from) / to;
}

// --- pace / speed -----------------------------------------------------------
// Canonical base is metres per second. Pace units (time per distance) are the
// inverse of speed, so they cannot be expressed as a single linear factor.

const PACE: Record<string, { toMs: (v: number) => number; fromMs: (v: number) => number }> = {
  'm-s': { toMs: (v) => v, fromMs: (v) => v },
  'km-h': { toMs: (v) => v / 3.6, fromMs: (v) => v * 3.6 },
  mph: { toMs: (v) => v * 0.44704, fromMs: (v) => v / 0.44704 },
  'min-km': { toMs: (v) => 1000 / (v * 60), fromMs: (v) => 1000 / (v * 60) },
  'min-mile': { toMs: (v) => 1609.344 / (v * 60), fromMs: (v) => 1609.344 / (v * 60) },
};

function convertPace(value: number, fromId: string, toId: string): number {
  const from = PACE[fromId];
  const to = PACE[toId];
  if (!from || !to) return Number.NaN;
  const ms = from.toMs(value);
  if (!Number.isFinite(ms) || ms <= 0) return Number.NaN;
  return to.fromMs(ms);
}

export const EXTRA_UNIT_CONFIG: Record<ExtraUnitVariant, ExtraUnitConfig> = {
  'digital-converter': {
    badge: 'Digital storage',
    heading: 'Digital Storage Converter',
    subtitle:
      'Convert between bits, bytes, kilobytes, megabytes, gigabytes and beyond, in decimal (KB) or binary (KiB) base.',
    measure: 'Data',
    hasBaseToggle: true,
    units: [
      { id: 'bit', name: 'Bit', symbol: 'b' },
      { id: 'byte', name: 'Byte', symbol: 'B' },
      { id: 'kilobit', name: 'Kilobit', symbol: 'Kb' },
      { id: 'kilobyte', name: 'Kilobyte', symbol: 'KB' },
      { id: 'megabit', name: 'Megabit', symbol: 'Mb' },
      { id: 'megabyte', name: 'Megabyte', symbol: 'MB' },
      { id: 'gigabit', name: 'Gigabit', symbol: 'Gb' },
      { id: 'gigabyte', name: 'Gigabyte', symbol: 'GB' },
      { id: 'terabyte', name: 'Terabyte', symbol: 'TB' },
      { id: 'petabyte', name: 'Petabyte', symbol: 'PB' },
    ],
    defaultFrom: 'megabyte',
    defaultTo: 'gigabyte',
    sampleValue: 1024,
    convert: convertDigital,
  },
  'each-converter': {
    badge: 'Counting units',
    heading: 'Each Converter',
    subtitle:
      'Convert counting quantities: each, pair, dozen, score, gross, great gross, ream and more.',
    measure: 'Quantity',
    hasBaseToggle: false,
    units: [
      { id: 'each', name: 'Each', symbol: 'ea' },
      { id: 'pair', name: 'Pair', symbol: 'pr' },
      { id: 'half-dozen', name: 'Half-dozen', symbol: '6' },
      { id: 'dozen', name: 'Dozen', symbol: 'dz' },
      { id: 'bakers-dozen', name: "Baker's dozen", symbol: '13' },
      { id: 'score', name: 'Score', symbol: '20' },
      { id: 'gross', name: 'Gross', symbol: 'gr' },
      { id: 'great-gross', name: 'Great gross', symbol: 'ggr' },
      { id: 'ream', name: 'Ream', symbol: 'rm' },
    ],
    defaultFrom: 'dozen',
    defaultTo: 'each',
    sampleValue: 1,
    convert: (value, fromId, toId) => convertLinear(EACH_FACTOR, value, fromId, toId),
  },
  'pace-converter': {
    badge: 'Pace & speed',
    heading: 'Pace Converter',
    subtitle:
      'Convert running pace and speed: min/km, min/mile, km/h, mph and m/s, with pace treated as the inverse of speed.',
    measure: 'Pace / speed',
    hasBaseToggle: false,
    units: [
      { id: 'min-km', name: 'Minutes per kilometre', symbol: 'min/km' },
      { id: 'min-mile', name: 'Minutes per mile', symbol: 'min/mi' },
      { id: 'km-h', name: 'Kilometres per hour', symbol: 'km/h' },
      { id: 'mph', name: 'Miles per hour', symbol: 'mph' },
      { id: 'm-s', name: 'Metres per second', symbol: 'm/s' },
    ],
    defaultFrom: 'min-km',
    defaultTo: 'km-h',
    sampleValue: 5,
    convert: (value, fromId, toId) => convertPace(value, fromId, toId),
  },
  'parts-per-converter': {
    badge: 'Concentration',
    heading: 'Parts-Per Converter',
    subtitle:
      'Convert concentration units: percent, per mille, ppm, ppb, ppt, plus mg/kg and mg/L equivalents.',
    measure: 'Concentration',
    hasBaseToggle: false,
    units: [
      { id: 'percent', name: 'Percent', symbol: '%' },
      { id: 'per-mille', name: 'Per mille', symbol: '‰' },
      { id: 'ppm', name: 'Parts per million', symbol: 'ppm' },
      { id: 'ppb', name: 'Parts per billion', symbol: 'ppb' },
      { id: 'ppt', name: 'Parts per trillion', symbol: 'ppt' },
      { id: 'mg-kg', name: 'Milligram per kilogram', symbol: 'mg/kg' },
      { id: 'mg-l', name: 'Milligram per litre', symbol: 'mg/L' },
    ],
    defaultFrom: 'percent',
    defaultTo: 'ppm',
    sampleValue: 1,
    convert: (value, fromId, toId) => convertLinear(PARTS_FACTOR, value, fromId, toId),
  },
};

// Human-friendly number formatting that stays readable across very large and
// very small magnitudes without losing precision.
export function formatExtraNumber(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (value === 0) return '0';
  const abs = Math.abs(value);
  if (abs >= 1e15 || abs < 1e-6) {
    return value.toExponential(6).replace(/\.?0+e/, 'e');
  }
  const rounded = Number(value.toPrecision(10));
  return rounded.toLocaleString('en-US', { maximumFractionDigits: 8 });
}
